import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { Checkbox } from '@/components/ui/checkbox';
import { Listbox } from '@/components/ui/listbox';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { api } from '@/lib/api';

export const DOCUMENT_VISIBILITY_OPTIONS = [
  'staff',
  'group',
  'committee',
  'department',
  'council',
  'ministers',
  'leaders',
  'members',
] as const;

export type DocumentVisibilityOption = (typeof DOCUMENT_VISIBILITY_OPTIONS)[number];
export type DocumentVisibility = DocumentVisibilityOption | 'leadership';

export type DocumentAudienceTarget = {
  type: 'group' | 'committee' | 'department' | 'unit' | 'council' | 'ministers';
  id: string | null;
};

export type DocumentAudienceDraft = {
  groupIds: string[];
  committeeIds: string[];
  departmentIds: string[];
  unitIds: string[];
  includeCouncil: boolean;
  includeMinisters: boolean;
};

export const emptyAudienceDraft = (): DocumentAudienceDraft => ({
  groupIds: [],
  committeeIds: [],
  departmentIds: [],
  unitIds: [],
  includeCouncil: false,
  includeMinisters: false,
});

export const displayVisibility = (visibility: DocumentVisibility): DocumentVisibilityOption =>
  visibility === 'leadership' ? 'leaders' : visibility;

export const audienceDraftFromTargets = (
  targets: DocumentAudienceTarget[] | undefined,
): DocumentAudienceDraft => {
  const draft = emptyAudienceDraft();
  for (const target of targets ?? []) {
    if (target.type === 'group' && target.id) draft.groupIds.push(target.id);
    if (target.type === 'committee' && target.id) draft.committeeIds.push(target.id);
    if (target.type === 'department' && target.id) draft.departmentIds.push(target.id);
    if (target.type === 'unit' && target.id) draft.unitIds.push(target.id);
    if (target.type === 'council') draft.includeCouncil = true;
    if (target.type === 'ministers') draft.includeMinisters = true;
  }
  return draft;
};

export const audienceTargetsFromDraft = (
  visibility: DocumentVisibility,
  draft: DocumentAudienceDraft,
): DocumentAudienceTarget[] => {
  const kind = displayVisibility(visibility);
  const targets: DocumentAudienceTarget[] = [];
  const includeBodies = kind === 'group' || kind === 'leaders';
  const includeCommittees = kind === 'committee' || kind === 'leaders';
  const includeDepartments = kind === 'department' || kind === 'leaders';

  if (includeBodies) {
    for (const id of draft.groupIds) targets.push({ type: 'group', id });
  }
  if (includeCommittees) {
    for (const id of draft.committeeIds) targets.push({ type: 'committee', id });
  }
  if (includeDepartments) {
    for (const id of draft.departmentIds) targets.push({ type: 'department', id });
    for (const id of draft.unitIds) targets.push({ type: 'unit', id });
  }
  if (kind === 'leaders') {
    if (draft.includeCouncil) targets.push({ type: 'council', id: null });
    if (draft.includeMinisters) targets.push({ type: 'ministers', id: null });
  }
  return targets;
};

export const isAudienceReady = (
  visibility: DocumentVisibility,
  draft: DocumentAudienceDraft,
): boolean => {
  const kind = displayVisibility(visibility);
  if (kind === 'group') return draft.groupIds.length > 0;
  if (kind === 'committee') return draft.committeeIds.length > 0;
  if (kind === 'department') return draft.departmentIds.length > 0 || draft.unitIds.length > 0;
  return true;
};

type NamedRow = {
  id: string;
  name: string;
  type?: string;
  label?: string | null;
  departmentId?: string;
};

interface DocumentVisibilityFieldsProps {
  visibility: DocumentVisibility;
  onVisibilityChange: (value: DocumentVisibilityOption) => void;
  audience: DocumentAudienceDraft;
  onAudienceChange: (value: DocumentAudienceDraft) => void;
}

export const DocumentVisibilityFields = ({
  visibility,
  onVisibilityChange,
  audience,
  onAudienceChange,
}: DocumentVisibilityFieldsProps) => {
  const { t } = useTranslation('documents');
  const { t: tCommon } = useTranslation();
  const kind = displayVisibility(visibility);

  const showGroups = kind === 'group' || kind === 'leaders';
  const showCommittees = kind === 'committee' || kind === 'leaders';
  const showDepartments = kind === 'department' || kind === 'leaders';
  const showLeaderFlags = kind === 'leaders';

  const groups = useQuery({
    queryKey: ['documents', 'audience', 'groups'],
    queryFn: () =>
      api.get<{ data: NamedRow[] }>('/groups', {
        query: { page: 1, pageSize: 100, category: 'group' },
      }),
    enabled: showGroups,
  });
  const committees = useQuery({
    queryKey: ['documents', 'audience', 'committees'],
    queryFn: () =>
      api.get<{ data: NamedRow[] }>('/groups', {
        query: { page: 1, pageSize: 100, category: 'committee' },
      }),
    enabled: showCommittees,
  });
  const departments = useQuery({
    queryKey: ['documents', 'audience', 'departments'],
    queryFn: () =>
      api.get<{ data: NamedRow[] }>('/people/departments', {
        query: { page: 1, pageSize: 100 },
      }),
    enabled: showDepartments,
  });
  const units = useQuery({
    queryKey: ['documents', 'audience', 'units', audience.departmentIds],
    queryFn: () =>
      api.get<{ data: NamedRow[] }>('/people/department-units', {
        query: {
          page: 1,
          pageSize: 100,
          departmentIds: audience.departmentIds.join(','),
        },
      }),
    enabled: showDepartments && audience.departmentIds.length > 0,
  });

  const setAudience = (patch: Partial<DocumentAudienceDraft>) =>
    onAudienceChange({ ...audience, ...patch });

  const multiLabels = {
    placeholder: t('audience.choose'),
    emptyMessage: t('audience.empty'),
    searchPlaceholder: tCommon('search.placeholder'),
    selectAllLabel: tCommon('action.selectAll'),
    clearAllLabel: tCommon('action.clearSelection'),
    summaryLabel: (count: number) => t('audience.selectedCount', { count }),
  };

  return (
    <div className="flex flex-col gap-4">
      <FormField label={t('fields.visibility')} required hint={t(`visibilityHint.${kind}`)}>
        {(field) => (
          <Listbox
            id={field.id}
            value={kind}
            onChange={(value) => {
              const next = (value ?? 'staff') as DocumentVisibilityOption;
              onVisibilityChange(next);
              if (next !== 'leaders') {
                onAudienceChange({
                  ...audience,
                  includeCouncil: false,
                  includeMinisters: false,
                });
              }
            }}
            options={DOCUMENT_VISIBILITY_OPTIONS.map((value) => ({
              value,
              label: t(`visibility.${value}`),
            }))}
            popoverPortal="dialog"
            aria-describedby={field['aria-describedby']}
          />
        )}
      </FormField>

      {showGroups ? (
        <FormField label={t('audience.groups')}>
          {(field) => (
            <MultiSelectListbox
              id={field.id}
              value={audience.groupIds}
              onChange={(groupIds) => setAudience({ groupIds })}
              options={(groups.data?.data ?? []).map((row) => ({
                value: row.id,
                label:
                  row.type === 'template'
                    ? t('audience.smartGroup', { name: row.name })
                    : row.name,
              }))}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
              {...multiLabels}
            />
          )}
        </FormField>
      ) : null}

      {showCommittees ? (
        <FormField label={t('audience.committees')}>
          {(field) => (
            <MultiSelectListbox
              id={field.id}
              value={audience.committeeIds}
              onChange={(committeeIds) => setAudience({ committeeIds })}
              options={(committees.data?.data ?? []).map((row) => ({
                value: row.id,
                label: row.name,
              }))}
              popoverPortal="dialog"
              aria-describedby={field['aria-describedby']}
              {...multiLabels}
            />
          )}
        </FormField>
      ) : null}

      {showDepartments ? (
        <>
          <FormField label={t('audience.departments')}>
            {(field) => (
              <MultiSelectListbox
                id={field.id}
                value={audience.departmentIds}
                onChange={(departmentIds) =>
                  setAudience({
                    departmentIds,
                    unitIds: audience.unitIds.filter((id) =>
                      (units.data?.data ?? []).some(
                        (unit) =>
                          unit.id === id &&
                          unit.departmentId !== undefined &&
                          departmentIds.includes(unit.departmentId),
                      ),
                    ),
                  })
                }
                options={(departments.data?.data ?? []).map((row) => ({
                  value: row.id,
                  label: row.name,
                }))}
                popoverPortal="dialog"
                aria-describedby={field['aria-describedby']}
                {...multiLabels}
              />
            )}
          </FormField>
          <FormField label={t('audience.units')} hint={t('audience.unitsHint')}>
            {(field) => (
              <MultiSelectListbox
                id={field.id}
                value={audience.unitIds}
                onChange={(unitIds) => setAudience({ unitIds })}
                options={(units.data?.data ?? []).map((row) => ({
                  value: row.id,
                  label: row.label ?? row.name,
                }))}
                disabled={audience.departmentIds.length === 0}
                popoverPortal="dialog"
                aria-describedby={field['aria-describedby']}
                {...multiLabels}
                placeholder={
                  audience.departmentIds.length === 0
                    ? t('audience.unitsNeedDepartment')
                    : multiLabels.placeholder
                }
              />
            )}
          </FormField>
        </>
      ) : null}

      {showLeaderFlags ? (
        <div className="flex flex-col gap-3">
          <label className="flex items-start gap-3 text-body">
            <Checkbox
              checked={audience.includeCouncil}
              onCheckedChange={(checked) => setAudience({ includeCouncil: checked === true })}
              className="mt-0.5"
            />
            <span>
              <span className="block">{t('audience.council')}</span>
              <span className="block text-caption text-fg-secondary">
                {t('audience.councilHint')}
              </span>
            </span>
          </label>
          <label className="flex items-start gap-3 text-body">
            <Checkbox
              checked={audience.includeMinisters}
              onCheckedChange={(checked) => setAudience({ includeMinisters: checked === true })}
              className="mt-0.5"
            />
            <span>
              <span className="block">{t('audience.ministers')}</span>
              <span className="block text-caption text-fg-secondary">
                {t('audience.ministersHint')}
              </span>
            </span>
          </label>
        </div>
      ) : null}
    </div>
  );
};
